<script setup lang="ts">
definePageMeta({ layout: 'marketing' })
</script>

<template>
  <MarketingVerticalLandingPage
    vertical="education-agencies"
    accent_from="from-blue-500"
    accent_to="to-indigo-500"
    :hero="{
      eyebrow: 'For AU education agents (CRICOS-aware)',
      headline: 'University placement + student visas, on one timeline.',
      subhead: 'CoE tracking, GTE drafting, OSHC arrangement, and the subclass 500 lodgement — handled on a single application file, with the provider commission tracked next to the case.',
      cta_text: 'See the placement workflow',
    }"
    :features="[
      { icon: '🎓', title: 'Provider + CRICOS aware',     description: 'Provider name, CRICOS provider code, course name, CRICOS course code, intake date — all first-class fields on every application.' },
      { icon: '📄', title: 'Offer → CoE → Visa pipeline', description: 'Conditional offer, full offer, tuition deposit, CoE issued, then subclass 500. Each stage notifies the student automatically.' },
      { icon: '🧾', title: 'GTE + financial evidence',    description: 'GTE statement drafted flag, financial capacity evidence slot, OSHC provider field — everything DHA wants to see, where you can find it.' },
      { icon: '💸', title: 'Provider commission tracked', description: 'AUD commission field per application. Pipe it into Xero or export to CSV. Stop reconciling commission across 12 partners by hand.' },
      { icon: '💬', title: 'Mobile-friendly portal',      description: 'Students upload transcripts and passport scans from their phone. Magic-link portal — no new password, no “app required”.' },
      { icon: '🌏', title: 'Multilingual counsellor team', description: 'Internal notes, public form copy, and message templates can all live in your operating language. Mongolian, Mandarin, Vietnamese — your call.' },
    ]"
    :workflow_steps="[
      { step: '1', title: 'Enquiry + counselling', description: 'Student fills your enquiry form → application opens → counsellor booking. Realistic course matches in under 48 hours.' },
      { step: '2', title: 'Provider application + offer', description: 'Apply to the provider, conditional or full offer received, tuition deposit, then CoE issued — each step on the same file.' },
      { step: '3', title: 'Visa + enrolled',       description: 'GTE drafted, financials uploaded, OSHC arranged, subclass 500 lodged. Grant notice closes the loop. Student arrives enrolled.' },
    ]"
    :pain_points="[
      'CoEs tracked in one spreadsheet, visas in another, commission in a third',
      'GTE statements rewritten from scratch every time because nothing was saved',
      'Pathway students get lost between the diploma provider and the target university',
      'No idea which provider owes you commission this month',
    ]"
    :relief_points="[
      'One application file from enquiry → enrolled, with CoE and visa stages on the same timeline',
      'GTE statements drafted from message templates, not from a blank page',
      'Pathway packages tracked as a single application across two providers',
      'Commission per application, exportable to Xero or CSV',
    ]"
    :sample_screen="{
      title: 'Erdene, B. — Bachelor of IT',
      subtitle: 'Application #4172',
      items: [
        { label: 'Provider',     value: 'University of Melbourne' },
        { label: 'CRICOS course',value: '093456A' },
        { label: 'Intake',       value: 'Feb 2027' },
        { label: 'Offer',        value: 'Full (issued 12 Aug)' },
        { label: 'CoE',          value: 'Issued 02 Sep 2026' },
      ],
    }"
    :faq="[
      { q: 'Does it talk to PRISMS / StudyLink?',           a: 'No direct integration today — you still issue CoEs in PRISMS via the provider. But every field DHA cares about (CRICOS codes, CoE number, intake) lives on the application file.' },
      { q: 'Can my partner universities see anything?',     a: 'Not by default — agencies own the case file. You can share a per-application link if a provider asks, but providers don’t get tenant access.' },
      { q: 'What about commission across multiple partners?',a: 'Commission is a field on each application, currency-aware in AUD. Filter by provider, export to CSV, or pipe to Xero — whichever fits your accounting.' },
      { q: 'Do you handle the subclass 500 paperwork?',     a: 'It tracks every required document — GTE statement, financial capacity, OSHC, medicals, police clearance — and the lodgement deadline. Lodgement itself happens through ImmiAccount; the file makes sure nothing’s missing first.' },
      { q: 'Multi-language UI for our counsellors?',        a: 'Internal labels and form copy can live in your operating language. The platform UI ships English-first today, with Mongolian and Mandarin in the locale switcher.' },
    ]"
    pricing_callout="One application file. Enquiry to enrolled."
  />
</template>
